/*
 * Maison theme license check.
 *
 * Posts the shop domain + license key to the license server once per
 * browser session, caches the result in sessionStorage, and surfaces a
 * banner if the response is explicitly invalid. Fails open: if the
 * server is unreachable, the storefront is never blocked.
 *
 * Two severities:
 *   gentle    — for `missing` (buyer hasn't pasted their key yet).
 *               Black bar, dismissible, respects per-session dismiss flag.
 *   critical  — for `revoked` and `shop_mismatch` (rights problem).
 *               Deep red bar, not dismissible, includes a contact link,
 *               re-renders on every pageview until the server says valid.
 *
 * The merchant enters their license key in Theme settings; the Liquid
 * template injects it as a global via <script>window.MAISON_LICENSE</script>.
 */
(function () {
  'use strict';

  var cfg = window.MAISON_LICENSE || {};
  var key = (cfg.key || '').trim();
  var endpoint = cfg.endpoint || 'https://maison-license.tractionify.workers.dev/check';
  var shop = (window.Shopify && window.Shopify.shop) || '';
  var copy = cfg.copy || {};
  var contactUrl = cfg.contactUrl || 'mailto:support@tractionify.com';
  var cacheKey = 'maison-license:' + shop + ':' + key;
  var dismissKey = 'maison-license-dismissed';

  if (!shop) return;

  var CRITICAL_REASONS = { revoked: 1, shop_mismatch: 1 };

  function severityFor(reason) {
    return CRITICAL_REASONS[reason] ? 'critical' : 'gentle';
  }

  function messageFor(reason) {
    if (reason === 'revoked') return copy.revoked || 'This theme license has been revoked.';
    if (reason === 'shop_mismatch') return copy.shop_mismatch || 'This license is bound to a different store.';
    if (reason === 'missing') return copy.missing || 'Theme not activated.';
    return copy.invalid || 'License not valid for this store.';
  }

  function showBanner(message, severity) {
    if (document.querySelector('[data-maison-license-banner]')) return;

    var critical = severity === 'critical';
    var el = document.createElement('div');
    el.setAttribute('data-maison-license-banner', '');
    el.setAttribute('data-severity', severity);
    el.setAttribute('role', critical ? 'alert' : 'status');

    // In-document-flow at the top of <body> so it scrolls away with the
    // page and never occludes the sticky header.
    var bg = critical ? '#7A1A1A' : '#0a0a0a';
    var pad = critical ? '14px 16px' : '10px 16px';
    var font = critical
      ? '600 14px/1.5 system-ui,sans-serif'
      : '500 13px/1.4 system-ui,sans-serif';

    el.style.cssText = [
      'padding:' + pad,
      'background:' + bg,
      'color:#fff',
      'font:' + font,
      'letter-spacing:0.04em',
      'display:flex',
      'gap:16px',
      'align-items:center',
      'justify-content:center',
      'text-align:center',
      'flex-wrap:wrap'
    ].join(';');

    var text = document.createElement('span');
    text.textContent = message;
    el.appendChild(text);

    if (critical) {
      var link = document.createElement('a');
      link.href = contactUrl;
      link.textContent = copy.contact_action || 'Contact theme supplier';
      link.style.cssText = [
        'color:#fff',
        'text-decoration:underline',
        'text-underline-offset:2px',
        'font-weight:600',
        'letter-spacing:0.04em'
      ].join(';');
      el.appendChild(link);
    } else {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = copy.dismiss || 'Dismiss';
      btn.style.cssText = [
        'background:transparent',
        'border:1px solid rgba(255,255,255,0.4)',
        'color:#fff',
        'padding:4px 10px',
        'font:inherit',
        'cursor:pointer',
        'letter-spacing:0.04em'
      ].join(';');
      btn.addEventListener('click', function () {
        sessionStorage.setItem(dismissKey, '1');
        el.remove();
      });
      el.appendChild(btn);
    }

    // Insert after the skip-to-content link so keyboard users still hit
    // skip first; visually appears at top of the page.
    var skip = document.querySelector('.skip-to-content');
    if (skip && skip.nextSibling) {
      document.body.insertBefore(el, skip.nextSibling);
    } else {
      document.body.insertBefore(el, document.body.firstChild);
    }
  }

  function handleResult(result) {
    if (!result || result.valid !== false) return;
    var severity = severityFor(result.reason);
    // Gentle banners respect the per-session dismiss; critical banners don't.
    if (severity === 'gentle' && sessionStorage.getItem(dismissKey) === '1') return;
    showBanner(messageFor(result.reason), severity);
  }

  // Fast path: replay cached session result. Critical reasons skip the cache
  // — we want re-checks on every pageview so reinstatement clears the banner
  // without requiring a fresh session.
  try {
    var cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      var parsed = JSON.parse(cached);
      if (!parsed || parsed.valid !== false || !CRITICAL_REASONS[parsed.reason]) {
        handleResult(parsed);
        return;
      }
    }
  } catch (e) { /* ignore */ }

  // No key configured. The banner is shown from the local result rather than
  // the response, so the nudge appears instantly and still appears if the
  // server is unreachable. The report is fire-and-forget alongside it: an
  // unlicensed install is invisible otherwise, and the privacy policy covers
  // sending the shop domain for this purpose. Nothing else is sent.
  if (!key) {
    var result = { valid: false, reason: 'missing' };
    try { sessionStorage.setItem(cacheKey, JSON.stringify(result)); } catch (e) {}
    handleResult(result);

    try {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: shop, license: '' }),
        credentials: 'omit',
        mode: 'cors',
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* never let reporting break the storefront */ }

    return;
  }

  // Slow path: phone home.
  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shop: shop, license: key }),
    credentials: 'omit',
    mode: 'cors',
    keepalive: true
  })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      if (!data) return;
      // Only cache non-critical results; critical re-checks on next pageview.
      if (data.valid !== false || !CRITICAL_REASONS[data.reason]) {
        try { sessionStorage.setItem(cacheKey, JSON.stringify(data)); } catch (e) {}
      }
      handleResult(data);
    })
    .catch(function () { /* fail open */ });
})();
